import { EpisodeTextImportNode } from '../models/EpisodeTextImportNode';
import { EpisodeTextImportStep } from '../models/EpisodeTextImportStep';
import { EpisodeTextEntityType } from '../types/episodeTextImportTypes';

/**
 * Strips tab characters from a field value before embedding it in a TSV column.
 * Tabs in field values would shift subsequent columns (including the real-ID tag in col3),
 * causing `parseRealId` to return `undefined` and breaking ID round-trip.
 * Tabs entered via paste (Ctrl+V from Word/Excel) are the primary source.
 */
function sanitizeField(value: string): string {
  return value.replace(/\t/g, ' ');
}

/**
 * Serializes a single EpisodeTextImportNode (with its steps) back to TSV text.
 * The output includes the 4th-column real-ID tags so that a subsequent
 * `parseTextToEpisodeText()` call recovers `realBranchId` / `realStepId` / `realAnswerId`.
 *
 * The produced text must be parseable by `getRowType()` back to correct entity types.
 */
export function importNodeToTsv(node: EpisodeTextImportNode): string {
  const lines: string[] = [];

  // Node header line: col0=title, col1="" (must be empty for Node detection), col2=description, col3=__bid:N
  // Force col1 empty to ensure getRowType() detects this as a Node row
  const bidTag = node.realBranchId != null ? `\t__bid:${node.realBranchId}` : '';
  lines.push(`${sanitizeField(node.title)}\t\t${sanitizeField(node.description)}${bidTag}`);

  for (const step of node.steps) {
    lines.push(...stepToTsvLines(step));
  }

  return lines.join('\n');
}

function stepToTsvLines(step: EpisodeTextImportStep): string[] {
  const lines: string[] = [];
  const sidTag = step.realStepId != null ? `__sid:${step.realStepId}` : '';
  const aidTag = step.realAnswerId != null ? `__aid:${step.realAnswerId}` : '';
  const idTag = sidTag || aidTag;

  const sn = sanitizeField(step.speakerName);
  const st = sanitizeField(step.speakerText);
  const sd = sanitizeField(step.description);

  switch (step.type) {
    case EpisodeTextEntityType.GotoFromNode:
      // GotoFromNode rows have no own ID
      lines.push(`${sn}\t${st}\t${sd}`);
      break;

    case EpisodeTextEntityType.CheckConditionList: {
      // Main check line gets __sid, sub-items get no ID
      const col3Check = idTag ? `\t${idTag}` : '';
      lines.push(`${sn}\t${st}\t${sd}${col3Check}`);
      for (const condition of step.conditionListItems) {
        lines.push(`${sanitizeField(condition.speakerName)}\t${sanitizeField(condition.speakerText)}\t${sanitizeField(condition.description)}`);
      }
      break;
    }

    case EpisodeTextEntityType.CheckCondition:
      // Individual condition rows — no own ID (they're sub-items)
      lines.push(`${sn}\t${st}\t${sd}`);
      break;

    default: {
      // All other step types: col0=speaker, col1=text, col2=description, col3=idTag (if present)
      const col3 = idTag ? `\t${idTag}` : '';
      lines.push(`${sn}\t${st}\t${sd}${col3}`);
      break;
    }
  }

  return lines;
}
